import { COLORS } from "../shared/constants";
import { canDo } from "../shared/utils";
import { useState } from "react";
import * as api from "../api";

export default // ─── Reports & Export Section ─────────────────────────────────
function ReportsSection({ patient, treatments, currentUser, providers }) {
  const [generating, setGenerating] = useState(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [selectedTreatmentId, setSelectedTreatmentId] = useState("");
  const [deIdentified, setDeIdentified] = useState(false);
  const [message, setMessage] = useState(null);

  const allTx = treatments;

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleInsuranceReport = async () => {
    setGenerating("insurance");
    setMessage(null);
    try {
      const blob = await api.generateInsuranceReport(patient.id, {
        treatment_id: selectedTreatmentId || undefined,
      });
      const filename = `Insurance_Report_${patient.last_name}_${patient.first_name}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.pdf`;
      downloadBlob(blob, filename);
      setMessage({ type: "success", text: "Insurance report downloaded." });
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Failed to generate report." });
    }
    setGenerating(null);
  };

  const handleProviderReport = async () => {
    setShowProviderModal(true);
    setNarrativeLoading(true);
    setMessage(null);
    try {
      const res = await api.getNarrativeDraft(patient.id, {
        treatment_id: selectedTreatmentId || undefined,
      });
      setNarrative(res.narrative || "");
    } catch (e) {
      setNarrative("(Unable to generate draft — you can type your own narrative here.)");
    }
    setNarrativeLoading(false);
  };

  const handleGenerateProviderPdf = async () => {
    setGenerating("provider");
    try {
      const blob = await api.generateProviderReport(patient.id, {
        treatment_id: selectedTreatmentId || undefined,
        narrative: narrative || undefined,
      });
      const filename = `Provider_Report_${patient.last_name}_${patient.first_name}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.pdf`;
      downloadBlob(blob, filename);
      setShowProviderModal(false);
      setMessage({ type: "success", text: "Provider report downloaded." });
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Failed to generate report." });
    }
    setGenerating(null);
  };

  const handleExportSessions = async () => {
    setGenerating("export_sessions");
    setMessage(null);
    try {
      const params = { patient_id: patient.id };
      if (selectedTreatmentId) params.treatment_id = selectedTreatmentId;
      if (deIdentified) params.de_identified = "true";
      const blob = await api.exportSessions(params);
      const prefix = deIdentified ? "DeIdentified_" : "";
      const filename = `TMS_Sessions_${prefix}${patient.last_name}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
      downloadBlob(blob, filename);
      setMessage({ type: "success", text: "Session data exported." });
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Export failed. Check permissions." });
    }
    setGenerating(null);
  };

  const handleExportAssessments = async () => {
    setGenerating("export_assessments");
    setMessage(null);
    try {
      const params = { patient_id: patient.id };
      if (selectedTreatmentId) params.treatment_id = selectedTreatmentId;
      if (deIdentified) params.de_identified = "true";
      const blob = await api.exportAssessments(params);
      const prefix = deIdentified ? "DeIdentified_" : "";
      const filename = `TMS_Assessments_${prefix}${patient.last_name}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
      downloadBlob(blob, filename);
      setMessage({ type: "success", text: "Assessment data exported." });
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Export failed. Check permissions." });
    }
    setGenerating(null);
  };

  return (
    <div style={{ background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginTop: "20px" }}>
      <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, marginBottom: "16px" }}>
        Reports & Export
      </h3>

      {/* Treatment Filter */}
      {allTx.length > 1 && (
        <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
          <label style={{ fontSize: "13px", color: COLORS.textSecondary, fontWeight: "500" }}>Filter by treatment:</label>
          <select value={selectedTreatmentId} onChange={(e) => setSelectedTreatmentId(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text, background: COLORS.surface }}>
            <option value="">All treatments</option>
            {allTx.map((tx) => (
              <option key={tx.id} value={tx.id}>{tx.protocol_name} — {tx.treatment_spot} ({tx.status})</option>
            ))}
          </select>
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div style={{
          padding: "10px 14px", borderRadius: "8px", marginBottom: "14px", fontSize: "13px",
          background: message.type === "success" ? "#E8F8F0" : COLORS.dangerLight,
          color: message.type === "success" ? "#2E9E6A" : COLORS.danger,
          border: `1px solid ${message.type === "success" ? "#A7E8C5" : COLORS.dangerBorder}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Report Buttons */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "16px" }}>
        <button onClick={handleInsuranceReport} disabled={generating === "insurance"}
          style={{ padding: "10px 18px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#fff", fontSize: "13px", fontWeight: "600", cursor: generating ? "wait" : "pointer", opacity: generating ? 0.7 : 1, display: "flex", alignItems: "center", gap: "6px" }}>
          📄 {generating === "insurance" ? "Generating..." : "Insurance Report"}
        </button>
        <button onClick={handleProviderReport} disabled={!!generating}
          style={{ padding: "10px 18px", borderRadius: "8px", border: `1px solid ${COLORS.primary}`, background: COLORS.surface, color: COLORS.primary, fontSize: "13px", fontWeight: "600", cursor: generating ? "wait" : "pointer", opacity: generating ? 0.7 : 1, display: "flex", alignItems: "center", gap: "6px" }}>
          ✉️ {generating === "provider" ? "Generating..." : "Provider Report"}
        </button>
        {canDo(currentUser, "export_research_data") && (
          <>
            <button onClick={handleExportSessions} disabled={!!generating}
              style={{ padding: "10px 18px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "13px", fontWeight: "500", cursor: generating ? "wait" : "pointer", opacity: generating ? 0.7 : 1, display: "flex", alignItems: "center", gap: "6px" }}>
              📊 {generating === "export_sessions" ? "Exporting..." : "Export Sessions CSV"}
            </button>
            <button onClick={handleExportAssessments} disabled={!!generating}
              style={{ padding: "10px 18px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "13px", fontWeight: "500", cursor: generating ? "wait" : "pointer", opacity: generating ? 0.7 : 1, display: "flex", alignItems: "center", gap: "6px" }}>
              📊 {generating === "export_assessments" ? "Exporting..." : "Export Assessments CSV"}
            </button>
          </>
        )}
      </div>

      {/* De-identified toggle */}
      {canDo(currentUser, "export_research_data") && (
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: COLORS.textSecondary, cursor: "pointer" }}>
          <input type="checkbox" checked={deIdentified} onChange={(e) => setDeIdentified(e.target.checked)} style={{ width: "16px", height: "16px" }} />
          De-identified export (remove patient names)
        </label>
      )}

      {/* Provider Report Modal — Narrative Editor */}
      {showProviderModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: COLORS.surface, borderRadius: "14px", padding: "28px", width: "90%", maxWidth: "680px", maxHeight: "80vh", overflow: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, marginBottom: "4px" }}>
              Provider Report — Narrative Review
            </h3>
            <p style={{ fontSize: "12px", color: COLORS.textSecondary, marginBottom: "16px" }}>
              Review and edit the auto-generated narrative before generating the PDF.
            </p>
            {narrativeLoading ? (
              <div style={{ padding: "40px", textAlign: "center", color: COLORS.textSecondary }}>Generating narrative draft...</div>
            ) : (
              <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)}
                disabled={!canDo(currentUser, "edit_report_narrative")}
                style={{ width: "100%", minHeight: "220px", padding: "14px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "13px", lineHeight: "1.6", color: COLORS.text, resize: "vertical", fontFamily: "inherit", background: canDo(currentUser, "edit_report_narrative") ? "#fff" : COLORS.bg }} />
            )}
            {!canDo(currentUser, "edit_report_narrative") && (
              <p style={{ fontSize: "11px", color: COLORS.textSecondary, marginTop: "6px", fontStyle: "italic" }}>
                You do not have permission to edit the narrative. An MD can edit before generating.
              </p>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
              <button onClick={() => setShowProviderModal(false)}
                style={{ padding: "10px 20px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "13px", cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleGenerateProviderPdf} disabled={generating === "provider"}
                style={{ padding: "10px 20px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#fff", fontSize: "13px", fontWeight: "600", cursor: generating ? "wait" : "pointer", opacity: generating ? 0.7 : 1 }}>
                {generating === "provider" ? "Generating PDF..." : "Generate PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


